import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { DashboardPageContainer } from '@carcareers/components';
import { baseFileQuery, useJobseekerViewQuery } from '@carcareers/store';
import { useSnackbar } from '@carcareers/contexts';

import '@cyntler/react-doc-viewer/dist/index.css';

export const EmployerJobseekerResumeView: FC = () => {
  const { id } = useParams();
  const jobseekerId = id ? parseInt(id) : undefined;
  const { toggleSnackbar } = useSnackbar();
  const [documentData, setDocumentData] = useState<{ file: File; url: string } | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: jobseeker, isLoading } = useJobseekerViewQuery(jobseekerId ?? 0, { skip: !jobseekerId });

  const jobseekerInfo = useMemo(() => {
    const firstName = jobseeker?.user?.firstName || '';
    const lastName = jobseeker?.user?.lastName || '';
    const jobseekerName = firstName && lastName ? `${firstName} ${lastName}` : 'Unknown Jobseeker';
    const breadcrumbName = jobseekerName !== 'Unknown Jobseeker' ? `${jobseekerName} - Resume` : `Jobseeker #${jobseekerId}`;
    const downloadFileName = firstName && lastName ? `Resume_${firstName}_${lastName}` : `Resume_${jobseekerId}`;
    
    return { jobseekerName, breadcrumbName, downloadFileName };
  }, [jobseeker?.user?.firstName, jobseeker?.user?.lastName, jobseekerId]);

  const isDocFile = useMemo(() => {
    if (!documentData?.file.type) return false;
    return ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(
      documentData.file.type
    );
  }, [documentData?.file.type]);

  const showError = useCallback((message: string) => {
    toggleSnackbar('error', message);
  }, [toggleSnackbar]);

  const showSuccess = useCallback((message: string) => {
    toggleSnackbar('success', message);
  }, [toggleSnackbar]);

  const downloadResume = useCallback(async () => {
    if (!jobseeker?.resume?.id) {
      showError('No resume found for download.');
      return;
    }

    setIsDownloading(true);

    try {
      const response = await baseFileQuery({
        url: `/resume/${jobseeker.resume.id}/stream`,
        method: 'GET',
        headers: {
          Accept: 'application/octet-stream, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resume: ${response.status}`);
      }

      const blob = await response.blob();
      const fileName = jobseeker.resume.filename?.split('/').pop() || 'resume.pdf';
      const file = new File([blob], fileName, { type: blob.type });
      const url = window.URL.createObjectURL(file);

      setDocumentData({ file, url });
    } catch (error) {
      console.error('Error fetching resume:', error);
      showError('Error loading resume. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  }, [jobseeker?.resume?.id, jobseeker?.resume?.filename, showError]);

  const handleDownloadFile = useCallback(async () => {
    if (!documentData) {
      showError('No document available for download.');
      return;
    }

    try {
      const a = document.createElement('a');
      a.href = documentData.url;
      a.download = jobseekerInfo.downloadFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      showSuccess('Resume downloaded successfully.');
    } catch (error) {
      showError('Error downloading resume. Please try again later.');
    }
  }, [documentData, jobseekerInfo.downloadFileName, showError, showSuccess]);

  useEffect(() => {
    if (jobseeker?.resume?.id && !documentData && !isDownloading) {
      downloadResume();
    }
  }, [jobseeker?.resume?.id, documentData, isDownloading, downloadResume]);

  useEffect(() => {
    return () => {
      if (documentData?.url) {
        window.URL.revokeObjectURL(documentData.url);
      }
    };
  }, [documentData?.url]);

  if (isLoading || isDownloading) {
    return (
      <DashboardPageContainer>
        <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {isLoading ? 'Loading jobseeker data...' : 'Loading resume document...'}
          </Typography>
        </Box>
      </DashboardPageContainer>
    );
  }

  if (!jobseeker) {
    return <Alert severity="error">Jobseeker not found.</Alert>;
  }

  if (!documentData) {
    return <Alert severity="error">Resume file could not be loaded.</Alert>;
  }

  return (
    <DashboardPageContainer idReplacement={jobseekerInfo.breadcrumbName}>
      {!isDocFile ? (
        <iframe
          src={documentData.url}
          title="PDF Viewer"
          width="100%"
          style={{ marginBottom: '16px', borderRadius: '12px', border: 0, flex: 1 }}
        >
          This browser does not support PDFs. Please download the PDF to view it:
          <a href={documentData.url}>Download PDF</a>
        </iframe>
      ) : (
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            backgroundColor: 'background.paper',
          }}
        >
          <Alert severity="info" sx={{ mb: 3, maxWidth: 600 }}>
            <Typography variant="body1" gutterBottom>
              <strong>DOCX File Preview Not Available</strong>
            </Typography>
            <Typography variant="body2">
              Word document preview is not supported in the browser. Please download the file to view it in Microsoft
              Word or a compatible application.
            </Typography>
          </Alert>
          <Typography variant="h6" gutterBottom>
            {documentData.file.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            File size: {(documentData.file.size / 1024).toFixed(1)} KB
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadFile}
            sx={{ mt: 2 }}
          >
            Download to View
          </Button>
        </Box>
      )}
    </DashboardPageContainer>
  );
};

export default EmployerJobseekerResumeView;
